import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Search, 
  Filter,
  Trash2,
  Eye,
  Clock
} from 'lucide-react'
import { useSwarms, useDeleteSwarm } from '../hooks/useSwarms'
import { formatDistanceToNow } from 'date-fns'
import type { Swarm, SwarmStatus } from '../types'

const statusOptions: { value: SwarmStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'idle', label: 'Idle' },
  { value: 'planning', label: 'Planning' },
  { value: 'executing', label: 'Executing' },
  { value: 'synthesizing', label: 'Synthesizing' },
  { value: 'complete', label: 'Complete' },
  { value: 'error', label: 'Error' },
]

export default function Swarms() {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<SwarmStatus | ''>('')
  const [page, setPage] = useState(1)
  
  const { data, isLoading } = useSwarms({ 
    status: statusFilter || undefined, 
    page
  })
  
  const deleteSwarm = useDeleteSwarm()

  const filteredSwarms = data?.swarms?.filter((swarm: any) =>
    swarm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    swarm.task.toLowerCase().includes(searchQuery.toLowerCase())
  ) || []

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this swarm?')) {
      deleteSwarm.mutate(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Swarms</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your agent swarms and view results
          </p>
        </div>
        <Link
          to="/swarms/create"
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Create Swarm
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search swarms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SwarmStatus | '')}
            className="w-full sm:w-48 pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Swarms Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSwarms.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No swarms found
                  </td>
                </tr>
              ) : (
                filteredSwarms.map((swarm: Swarm) => (
                  <tr key={swarm.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <Link
                          to={`/swarms/${swarm.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600"
                        >
                          {swarm.name}
                        </Link>
                        <span className="text-xs text-gray-500 truncate max-w-xs">
                          {swarm.task.substring(0, 60)}...
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={swarm.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {swarm.progress.totalAgents > 0 ? (
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{
                                width: `${Math.round(
                                  (swarm.progress.completedAgents / swarm.progress.totalAgents) * 100
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">
                            {swarm.progress.completedAgents}/{swarm.progress.totalAgents}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {formatDistanceToNow(new Date(swarm.createdAt))} ago
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/swarms/${swarm.id}`}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="View"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(swarm.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data?.total > 20 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {Math.ceil(data.total / 20)}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil(data.total / 20)}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: SwarmStatus }) {
  const statusClasses = {
    idle: 'bg-gray-100 text-gray-700',
    planning: 'bg-blue-100 text-blue-700',
    executing: 'bg-amber-100 text-amber-700',
    synthesizing: 'bg-purple-100 text-purple-700',
    complete: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusClasses[status]}`}>
      {status}
    </span>
  )
}
